import { create, insert, search, type Orama } from "@orama/orama";
import type { Container, ContainerItem, Item } from "@egalregal/shared";
import { effectiveSearchText, pathLabelOf, searchTokenizerConfig, DEFAULT_SEARCH_LANGUAGE, type SearchLanguage } from "@egalregal/shared";
import { containerSearchableText, itemSearchableText } from "./searchableText";

/**
 * Keyword fallback index (ADR-0022, B3). Built lazily over a snapshot of the
 * catalog plus the item mirror (ticket 08) and searched only when the embedding
 * model is unavailable (or the active mode is `keyword`). Kept as a plain side
 * table so the primary semantic path carries no dependency on it.
 *
 * Items index one doc per placement (each with the holding container's id for
 * the path) and one unplaced doc when the item has no placement — the same
 * per-placement result contract the server uses. The barcode stays out of the
 * searchable text; it is matched exactly through the `barcode:` filter.
 *
 * Containers and items index the server-maintained `data.searchText.text` when
 * its `version` matches `SEARCHABLE_TEXT_VERSION` (ADR-0036), else they fall
 * back to live extraction via `containerSearchableText` / `itemSearchableText`
 * — the exact string the semantic path embeds — so both engines match identical
 * phrases (ADR-0033) even before the pipeline has caught up.
 */

const schema = {
  id: "string",
  name: "string",
  kind: "string",
  description: "string",
  owner: "string",
  tags: "string[]",
  /** Type discriminator: "container" | "item". */
  docType: "string",
  /** Holding container id for item docs; "" for unplaced items. */
  __containerId: "string",
  /** Joined free-text blob used for keyword scoring. */
  searchable: "string",
  /** Joined path for display. */
  __path: "string",
} as const;
type CatalogSchema = typeof schema;

interface IndexDoc {
  id: string;
  name: string;
  kind: string;
  description: string;
  owner: string;
  tags: string[];
  docType: "container" | "item";
  __containerId: string;
  searchable: string;
  __path: string;
}

export interface KeywordHit {
  docType: "container" | "item";
  container: Container | null;
  item: Item | null;
  /** For item hits: the holding container id, or "" when unplaced. */
  containerId: string;
  path: string;
  score: number;
}

let db: Orama<CatalogSchema> | null = null;
let dbContainers: Container[] = [];
let dbItems: Item[] = [];
let dbPlacements: ContainerItem[] = [];
/** Base ids (container id / item id) of the docs in `db`; swapped with it. */
let dbDocIds = new Set<string>();
/**
 * Global Orama tokenizer language (ADR-0036 decision 6), read from the API at
 * boot (`setSearchLanguage`). Kept next to the cache and swapped like the rest,
 * so a value change rebuilds the index with the matching stemmer.
 */
let dbLanguage: SearchLanguage = DEFAULT_SEARCH_LANGUAGE;
let dbLanguageApplied: SearchLanguage = DEFAULT_SEARCH_LANGUAGE;

export function getIndexContainers(): Container[] {
  return dbContainers;
}

/**
 * Set the global Orama language for the keyword index (ADR-0036 decision 6),
 * applied at boot from `GET /api/auth/config`. A different value invalidates
 * the cached index so the next build stems with the new language.
 *
 * @param language short code from `SEARCH_LANGUAGES`.
 */
export function setSearchLanguage(language: SearchLanguage): void {
  dbLanguage = language;
}

/**
 * Whether `id` currently has a doc in the keyword index (ADR-0033 validation
 * view): containers are keyed by their own id, items by their id across every
 * per-placement doc. Read-only — nothing is built on read, and an empty set
 * before the first build reports "not indexed" truthfully.
 *
 * @param id container id or item id to look up.
 * @returns true when the current index holds at least one doc for `id`.
 */
export function hasKeywordDoc(id: string): boolean {
  return dbDocIds.has(id);
}

function itemDocs(items: Item[], placements: ContainerItem[], containers: Container[]): IndexDoc[] {
  const docs: IndexDoc[] = [];
  for (const item of items) {
    const placed = placements.filter((p) => p.itemId === item.id);
    const homes = placed.length > 0 ? placed.map((p) => p.containerId) : [""];
    const searchable = effectiveSearchText(item) ?? itemSearchableText(item).text;
    for (const containerId of homes) {
      docs.push({
        id: `${item.id}:${containerId || "unlinked"}`,
        name: item.name,
        kind: item.category,
        description: "",
        owner: "",
        tags: [],
        docType: "item",
        __containerId: containerId,
        searchable,
        __path: containerId ? pathLabelOf(containers, containerId) : "",
      });
    }
  }
  return docs;
}

/**
 * (Re)build the keyword index for a catalog + items snapshot. Idempotent per
 * array identity (and the tokenizer language): callers hand in fresh arrays
 * whenever the catalog or item mirror changes, so a stored search-text update
 * rebuilds the index.
 */
export async function ensureKeywordIndex(
  containers: Container[],
  items: Item[] = [],
  placements: ContainerItem[] = [],
): Promise<void> {
  if (
    db &&
    dbContainers === containers &&
    dbItems === items &&
    dbPlacements === placements &&
    dbLanguageApplied === dbLanguage
  ) {
    return;
  }
  const next = create({ schema, components: { tokenizer: await searchTokenizerConfig(dbLanguage) } });
  const docs: IndexDoc[] = [
    ...containers
      .filter((c) => !c.deleted)
      .map((c) => {
        const path = pathLabelOf(containers, c.id);
        return {
          id: c.id,
          name: c.name,
          kind: c.kind,
          description: c.description ?? "",
          owner: c.owner ?? "",
          tags: c.tags ?? [],
          docType: "container" as const,
          __containerId: "",
          searchable: effectiveSearchText(c) ?? containerSearchableText(c, path).text,
          __path: path,
        };
      }),
    ...itemDocs(items, placements, containers),
  ];
  for (const doc of docs) {
    await insert(next, doc);
  }
  db = next;
  dbContainers = containers;
  dbItems = items;
  dbPlacements = placements;
  dbLanguageApplied = dbLanguage;
  // One base id per doc: containers carry their id, items the `${item.id}:…`
  // per-placement id — membership questions ask by entity, not by placement.
  dbDocIds = new Set(docs.map((d) => (d.docType === "container" ? d.id : d.id.split(":")[0]!)));
}

/**
 * Keyword search over the current index. Returns full hits (containers and
 * items) so the router can apply the same deterministic tiebreak as the
 * semantic path and merge both result kinds on one score scale. The index is
 * built over the full catalog (paths need the whole tree); `candidates`
 * optionally gates which docs are returned (DSL-filter survivors).
 */
export async function keywordSearch(
  containers: Container[],
  items: Item[],
  query: string,
  limit = 20,
  placements: ContainerItem[] = [],
  candidates?: { containers?: Set<string>; items?: Set<string> },
): Promise<KeywordHit[]> {
  await ensureKeywordIndex(containers, items, placements);
  if (!db) return [];

  const byContainer = new Map(containers.map((c) => [c.id, c]));
  const byItem = new Map(items.map((i) => [i.id, i]));
  const results = await search<typeof db, IndexDoc>(db, {
    term: query,
    limit: 100,
  });
  const ranked: KeywordHit[] = [];
  for (const hit of results.hits) {
    const doc = hit.document;
    if (doc.docType === "container") {
      const container = byContainer.get(doc.id);
      if (!container) continue;
      if (candidates?.containers && !candidates.containers.has(doc.id)) continue;
      ranked.push({
        docType: "container",
        container,
        item: null,
        containerId: "",
        path: doc.__path,
        score: hit.score,
      });
    } else {
      const itemId = doc.id.split(":")[0]!;
      const item = byItem.get(itemId);
      if (!item) continue;
      if (candidates?.items && !candidates.items.has(itemId)) continue;
      ranked.push({
        docType: "item",
        container: null,
        item,
        containerId: doc.__containerId,
        path: doc.__path,
        score: hit.score,
      });
    }
  }
  return ranked.slice(0, limit);
}